import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Activity, UserPlus, LogIn, Calendar, Search, RefreshCcw, Bell, Shield, Clock } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const AdminActivity = () => {
    const { allUsers, refreshAllUsers } = useAuth();
    const [localUsers, setLocalUsers] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastSync, setLastSync] = useState(new Date());

    // Initial load
    useEffect(() => {
        if (allUsers?.length > 0) {
            setLocalUsers(allUsers);
        }
    }, [allUsers?.length === 0]); // Only if empty initially

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            if (typeof refreshAllUsers === 'function') {
                await refreshAllUsers();
            }
            setLocalUsers(allUsers);
            setLastSync(new Date());
            toast.success('Atividade atualizada!');
        } catch (err) {
            toast.error('Erro ao sincronizar atividade.');
        } finally {
            setIsRefreshing(false);
        }
    };

    // Process lists efficiently from LOCAL state
    const { recentRegistrations, recentLogins } = useMemo(() => {
        const users = [...localUsers];

        // 1. Last 40 Created Users
        const registered = users
            .filter(u => u.createdAt)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 40);

        // 2. Last 40 Logged-in Users
        const loggedIn = users
            .filter(u => u.lastLoginAt || u.lastLogin)
            .sort((a, b) => new Date(b.lastLoginAt || b.lastLogin) - new Date(a.lastLoginAt || a.lastLogin))
            .slice(0, 40);

        return {
            recentRegistrations: registered,
            recentLogins: loggedIn
        };
    }, [localUsers]);

    const formatDate = (dateString) => {
        if (!dateString) return 'Data desconhecida';
        try {
            return format(new Date(dateString), "d 'de' MMM, HH:mm", { locale: ptBR });
        } catch (e) {
            return 'Data inválida';
        }
    };

    const UserListItem = ({ user, type }) => (
        <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.05] hover:border-white/10 transition-all group">
            <div className="flex items-center gap-4">
                <div className="relative">
                    <Avatar className="h-12 w-12 border-2 border-white/5 group-hover:border-blue-500/30 transition-colors">
                        <AvatarImage src={user.avatar ? `https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png` : null} />
                        <AvatarFallback className="bg-gray-800 text-gray-400 font-bold">
                            {user.name?.charAt(0).toUpperCase() || '?'}
                        </AvatarFallback>
                    </Avatar>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-black ${type === 'register' ? 'bg-emerald-500' : 'bg-blue-500'}`} />
                </div>
                <div>
                    <h4 className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors">
                        {user.name || 'Usuário Desconhecido'}
                    </h4>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-gray-500 font-mono tracking-tighter">
                            {user.discordId}
                        </span>
                        {user.accessLevel !== 'user' && (
                            <span className="text-[9px] bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded uppercase font-black tracking-widest">
                                Staff
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2 text-[10px] text-gray-400 font-medium">
                    <Clock className="h-3 w-3 opacity-50" />
                    <span>
                        {type === 'register' ? formatDate(user.createdAt) : formatDate(user.lastLoginAt || user.lastLogin)}
                    </span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-700 pb-10">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 bg-teal-600/20 rounded-2xl border border-teal-500/30">
                            <Activity className="h-6 w-6 text-teal-400" />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-white">
                            ATIVIDADE <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500">REAL</span>
                        </h1>
                    </div>
                    <p className="text-gray-400 text-lg flex items-center gap-2 font-medium">
                        <Shield className="h-4 w-4 text-blue-500" />
                        Acompanhamento de fluxo de usuários no sistema.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <Button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="h-14 px-6 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-2xl transition-all group backdrop-blur-md"
                    >
                        <RefreshCcw className={`h-5 w-5 mr-3 transition-transform duration-700 ${isRefreshing ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                        <span className="font-black uppercase tracking-widest text-xs">Atualizar Agora</span>
                    </Button>

                    <div className="h-14 px-6 bg-black/60 backdrop-blur-xl rounded-2xl border border-white/10 flex flex-col justify-center">
                        <p className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-black">Última Sincronização</p>
                        <p className="text-xl font-mono font-black text-white leading-none mt-1">
                            {lastSync.toLocaleTimeString('pt-BR')}
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 relative z-10">
                {/* Last Registrations Column */}
                <Card className="bg-black/40 border-white/5 backdrop-blur-md rounded-[2.5rem] overflow-hidden flex flex-col h-[850px] shadow-2xl">
                    <CardHeader className="p-8 pb-4 border-b border-white/5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20">
                                    <UserPlus className="h-6 w-6 text-emerald-400" />
                                </div>
                                <div>
                                    <CardTitle className="text-xl font-black tracking-tight text-white uppercase">Novos Membros</CardTitle>
                                    <CardDescription className="text-gray-500 font-medium">Últimos 40 registros detectados</CardDescription>
                                </div>
                            </div>
                            <div className="px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-bold text-xs">
                                Total: {localUsers.length}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 flex-1 overflow-hidden">
                        <ScrollArea className="h-full pr-4 custom-scrollbar">
                            <div className="space-y-3">
                                {recentRegistrations.length > 0 ? (
                                    recentRegistrations.map((user) => (
                                        <UserListItem
                                            key={`reg-${user.discordId}`}
                                            user={user}
                                            type="register"
                                        />
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-600 gap-4">
                                        <Search className="h-12 w-12 opacity-20" />
                                        <p className="font-bold uppercase tracking-widest text-sm">Carregando dados...</p>
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Last Logins Column */}
                <Card className="bg-black/40 border-white/5 backdrop-blur-md rounded-[2.5rem] overflow-hidden flex flex-col h-[850px] shadow-2xl">
                    <CardHeader className="p-8 pb-4 border-b border-white/5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-3 rounded-2xl bg-blue-500/10 border border-blue-500/20">
                                    <LogIn className="h-6 w-6 text-blue-400" />
                                </div>
                                <div>
                                    <CardTitle className="text-xl font-black tracking-tight text-white uppercase">Fluxo de Acesso</CardTitle>
                                    <CardDescription className="text-gray-500 font-medium">Últimas 40 conexões ao sistema</CardDescription>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                                </span>
                                <span className="text-xs text-blue-400 font-bold uppercase tracking-widest">Atividade Ativa</span>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6 flex-1 overflow-hidden">
                        <ScrollArea className="h-full pr-4 custom-scrollbar">
                            <div className="space-y-3">
                                {recentLogins.length > 0 ? (
                                    recentLogins.map((user) => (
                                        <UserListItem
                                            key={`login-${user.discordId}`}
                                            user={user}
                                            type="login"
                                        />
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-20 text-gray-600 gap-4">
                                        <Search className="h-12 w-12 opacity-20" />
                                        <p className="font-bold uppercase tracking-widest text-sm">Carregando dados...</p>
                                    </div>
                                )}
                            </div>
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AdminActivity;
